import { unref } from "vue";
import { nl, enUS, fr, zhCN, it, es } from "date-fns/locale";
import { format, formatDuration } from "date-fns";
import { TZDate } from "@date-fns/tz";

/**
 * Converts a date string with a timezone offset or UTC (Z) to a TZDate object.
 *
 * @param dateString - The date string to be converted. It should be in the format "YYYY-MM-DDTHH:mm:ss.sssZ" or "YYYY-MM-DDTHH:mm:ss.sss+00:00".
 *
 * @returns {TZDate} - The converted TZDate object.
 */
export const dateToTimezoneDate = (dateString: string): TZDate => {
  const timezoneRegex = /([+-]\d{2}:\d{2}|Z)$/;
  const match =
    typeof dateString === "string" && dateString.match(timezoneRegex);
  if (!match) {
    throw new Error(
      "Invalid date string format. Expected format with timezone offset or UTC (Z).",
    );
  }
  const timezoneOffset = match[1] === "Z" ? "+00:00" : match[1];
  const date = new TZDate(dateString, timezoneOffset);

  return date;
};

/**
 * Formats a date using date-fns with the specified output format and locale.
 *
 * @param date - The date to be formatted. It can be a TZDate or a Date object.
 * @param outputFormat - The format string to use for formatting the date. It should be a valid date-fns format string. (see: https://date-fns.org/v4.1.0/docs/format)
 * @param toLocale - The locale to use for formatting the date. It can be a string or a ref. The default is "enUS".
 *
 * @returns {string} - The formatted date string.
 */
export const formatDate = (
  date: TZDate | Date,
  outputFormat: string,
  toLocale: MaybeRef<string> = "en",
): string => {
  const locale = {
    en: enUS,
    fr: fr,
    nl: nl,
    zh: zhCN,
    it: it,
    es: es,
  }[unref(toLocale)];

  if (!locale) {
    throw new Error(`Invalid locale: ${unref(toLocale)}`);
  }

  return format(date, outputFormat, {
    locale,
  });
};

export const formatDistance = (
  from: TZDate | Date,
  to: TZDate | Date,
  toLocale: MaybeRef<string> = "en",
) => {
  const locale = {
    en: enUS,
    fr: fr,
    nl: nl,
    zh: zhCN,
    it: it,
    es: es,
  }[unref(toLocale)];

  if (!locale) {
    throw new Error(`Invalid locale: ${unref(toLocale)}`);
  }

  const duration = {
    hours: Math.abs(from.getHours() - to.getHours()),
    minutes: Math.abs(from.getMinutes() - to.getMinutes()),
    seconds: Math.abs(from.getSeconds() - to.getSeconds()),
  };

  return formatDuration(duration, { locale });
};
